import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { RowsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import { resolveImage } from "util/requireImage";
import dataProducts from "../../data/products.json";
import "./ProductShowcase.scss";

export default function ProductShowcase() {
  const [activeProjectId, setActiveProjectId] = useState(
    dataProducts?.[0]?.id || "",
  );

  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [photos, setPhotos] = useState([]);

  const activeProject = dataProducts?.find(
    (project) => project.id === activeProjectId,
  );

  const product = activeProject?.products?.[0];
  const examples = product?.examples || [];

  useEffect(() => {
    let cancelled = false;

    const loadImages = async () => {
      const loadedPhotos = await Promise.all(
        examples.map(
          (image, index) =>
            new Promise((resolve) => {
              const src = resolveImage(image);
              const img = new Image();

              img.onload = () => {
                resolve({
                  src,
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                  alt: `${product?.name || "Product"} screen ${index + 1}`,
                });
              };

              img.onerror = () => {
                resolve({
                  src,
                  width: 1600,
                  height: 1000,
                  alt: `${product?.name || "Product"} screen ${index + 1}`,
                });
              };

              img.src = src;
            }),
        ),
      );

      if (!cancelled) {
        setPhotos(loadedPhotos);
      }
    };

    if (examples.length) {
      loadImages();
    } else {
      setPhotos([]);
    }

    return () => {
      cancelled = true;
    };
  }, [examples, product?.name]);

  return (
    <>
      <div className="showcase-container">
        <section className="header-section">
          <div className="logo-grid">
            {dataProducts?.map((project) => {
              const isActive = project.id === activeProjectId;

              return (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => {
                    setActiveProjectId(project.id);
                    setLightboxIndex(-1);
                  }}
                  className={`logo-btn ${
                    project.themeClass || ""
                  } ${isActive ? "active" : ""}`}
                  aria-pressed={isActive}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeBorder"
                      className="active-border"
                      transition={{
                        type: "spring",
                        stiffness: 320,
                        damping: 30,
                      }}
                    />
                  )}
                  <img
                    src={resolveImage(project.logo)}
                    className="logo-project"
                    alt="logo"
                  />
                  <span>{project.name}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="products-section">
          <div className="products-wrapper">
            <AnimatePresence mode="wait">
              {product ? (
                <motion.article
                  key={product.id}
                  className="product-showcase"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{
                    duration: 0.35,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <div className="product-header">
                    <motion.div
                      className="product-info"
                      initial={{ opacity: 0, x: -15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.35,
                        delay: 0.05,
                      }}
                    >
                      <span className="product-label">
                        {activeProject?.logoName}
                      </span>

                      <h2>{product.name}</h2>

                      <p>{product.desc}</p>

                      {/* {activeProject?.url && (
                        <a
                          href={activeProject.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-link"
                        >
                          <span>Visit Project</span>
                          <span className="project-link-icon">↗</span>
                        </a>
                      )} */}
                    </motion.div>

                    {/* {product.thumbnail && (
                      <motion.div
                        className="product-thumbnail"
                        initial={{
                          opacity: 0,
                          scale: 0.97,
                        }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                        }}
                        transition={{
                          duration: 0.4,
                          delay: 0.08,
                        }}
                      >
                        <img src={product.thumbnail} alt={product.name} />
                      </motion.div>
                    )} */}
                  </div>

                  {photos.length > 0 && (
                    <motion.section
                      className="examples-section"
                      initial={{
                        opacity: 0,
                        y: 15,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      transition={{
                        duration: 0.4,
                        delay: 0.1,
                      }}
                    >
                      <div className="examples-heading">
                        <div>
                          <span className="examples-label">
                            Product Preview
                          </span>

                          <p>Explore the interface and experience</p>
                        </div>

                        <span className="examples-count">
                          {String(photos.length).padStart(2, "0")} screens
                        </span>
                      </div>

                      <div className="photo-album">
                        <RowsPhotoAlbum
                          photos={photos}
                          targetRowHeight={180}
                          spacing={10}
                          rowConstraints={{
                            singleRowMaxHeight: 280,
                          }}
                          onClick={({ index }) => setLightboxIndex(index)}
                        />
                      </div>
                    </motion.section>
                  )}
                </motion.article>
              ) : (
                <motion.div
                  key="empty"
                  className="empty-state"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <span>Coming Soon</span>

                  <p>
                    We are currently preparing the product showcase for this
                    project.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </div>

      <Lightbox
        open={lightboxIndex >= 0}
        close={() => setLightboxIndex(-1)}
        index={lightboxIndex}
        slides={photos}
        controller={{
          closeOnBackdropClick: true,
        }}
        animation={{
          fade: 250,
          swipe: 300,
        }}
        carousel={{
          finite: false,
        }}
        styles={{
          container: {
            backgroundColor: "rgba(0, 0, 0, 0.94)",
          },
        }}
      />
    </>
  );
}
